// Identity section (US-106) — name, description (+ AI assist), website and
// the socials sub-list. JSX lifted verbatim from BusinessEditor; the socials
// list helpers stay owned by the parent (Wave D `useListEditor` wiring) and
// arrive as props.

import { useT } from '@barel/i18n';
import { BAiButton, Button, Card, FieldAssembly, Input, Select, Textarea } from '@barel/ui';

import { detectSocialKind, type BusinessDraft } from '../lib/defaults';
import { SectionHeader } from './SectionHeader';
import type { RunAi, SetField } from './types';

const SOCIAL_KINDS = [
  'linkedin',
  'twitter',
  'instagram',
  'tiktok',
  'youtube',
  'facebook',
  'other',
] as const;
type SocialKind = (typeof SOCIAL_KINDS)[number];

export interface IdentitySectionProps {
  name: BusinessDraft['name'];
  description: BusinessDraft['description'];
  website: BusinessDraft['website'];
  socials: BusinessDraft['socials'];
  onChange: SetField;
  updateSocial: (idx: number, patch: Partial<{ kind: string; url: string; label: string }>) => void;
  removeSocial: (idx: number) => void;
  addSocial: () => void;
  runAi: RunAi;
  aiBusyField: string | null;
}

export function IdentitySection({
  name,
  description,
  website,
  socials,
  onChange,
  updateSocial,
  removeSocial,
  addSocial,
  runAi,
  aiBusyField,
}: IdentitySectionProps) {
  const t = useT('businesses');

  return (
    <Card padding="lg" radius="2xl" data-testid="business-section-identity">
      <SectionHeader title={t('sections.identity')} subtitle={t('identity.subtitle')} />
      <div className="flex flex-col gap-4">
        <FieldAssembly label={t('identity.nameLabel')} required>
          <Input
            type="text"
            value={name}
            placeholder={t('identity.namePlaceholder')}
            onChange={(event) => onChange('name', event.target.value)}
            data-testid="business-name"
          />
        </FieldAssembly>
        <FieldAssembly
          label={t('identity.descriptionLabel')}
          action={
            <BAiButton
              onClick={() => void runAi('description')}
              busy={aiBusyField === 'description'}
              label={t('ai.improve')}
              busyLabel={t('ai.busy')}
              data-testid="business-description-ai"
            />
          }
        >
          <Textarea
            rows={4}
            value={description}
            placeholder={t('identity.descriptionPlaceholder')}
            onChange={(event) => onChange('description', event.target.value)}
            data-testid="business-description"
          />
        </FieldAssembly>
        <FieldAssembly label={t('identity.websiteLabel')}>
          <Input
            type="url"
            value={website}
            placeholder={t('identity.websitePlaceholder')}
            onChange={(event) => onChange('website', event.target.value)}
            data-testid="business-website"
          />
        </FieldAssembly>
        <FieldAssembly label={t('identity.socialsLabel')} hint={t('identity.socialsHint')}>
          <div className="flex flex-col gap-2">
            {socials.map((social, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Select
                  value={social.kind}
                  onValueChange={(value) => updateSocial(idx, { kind: value })}
                >
                  <Select.Trigger
                    aria-label={t('identity.socialKindLabel')}
                    data-testid={`business-social-kind-${idx}`}
                  >
                    <Select.Value />
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content>
                      {SOCIAL_KINDS.map((kind) => (
                        <Select.Item key={kind} value={kind}>
                          {t(`identity.kind.${kind}`)}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Portal>
                </Select>
                <Input
                  type="url"
                  value={social.url}
                  placeholder={t('identity.websitePlaceholder')}
                  onChange={(event) => {
                    const url = event.target.value;
                    // Auto-detect when the kind is still `other`.
                    const nextKind: SocialKind =
                      social.kind === 'other' && url
                        ? (detectSocialKind(url) as SocialKind)
                        : (social.kind as SocialKind);
                    updateSocial(idx, { url, kind: nextKind });
                  }}
                  data-testid={`business-social-url-${idx}`}
                />
                {social.kind === 'other' ? (
                  <Input
                    type="text"
                    value={social.label}
                    aria-label={t('identity.socialLabelLabel')}
                    placeholder={t('identity.socialLabelPlaceholder')}
                    onChange={(event) => updateSocial(idx, { label: event.target.value })}
                    data-testid={`business-social-label-${idx}`}
                  />
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={t('identity.removeSocial')}
                  onClick={() => removeSocial(idx)}
                >
                  ×
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addSocial}
              data-testid="business-social-add"
            >
              {t('identity.addSocial')}
            </Button>
          </div>
        </FieldAssembly>
      </div>
    </Card>
  );
}
